import * as GC from '@grapecity/spread-sheets';
import { IProduct, IProductTab, ISetUpProduct, ProductFactory } from '@models/product';
import { ProductStatus } from '@models/record-status';
import { setWatermark } from '@shared/spread/sheet-functions';
import { mapActionKeys, setGreenArea } from '@shared/spread/spread-action-keys';
import { colours, FONT_NAME, FONT_SIZE } from '@shared/spread/spread-settings';
import { suspendDoAndResume } from '@shared/spread/suspend-and-resume';
import { hasProductChanges } from './sheet-functions/helpers';

export const getSpreadContainer = () =>
  document && (document.querySelector('.product-setup-container .spreadsheet-container') as HTMLElement);

const MAX_PRODUCT_DIGITS = 11;

export const throttleTimeUnit = 100;
export const colourIndicatorsEnabled = true;

export enum COMPONENT_TOAST_TYPES {
  loadError = 'loadError',
  saveError = 'saveError',
  saveSuccess = 'saveSuccess',
  willCauseDataLoss = 'willCauseDataLoss',
  actionDeactivate = 'actionDeactivate',
  actionDeactivateUndo = 'actionDeactivateUndo',
  publishedTab = 'publishedTab',
  createTabError = 'createTabError'
}

export enum ROW_STATES {
  deactivated = 'deactivated',
  clean = 'clean',
  edit = 'edit',
  editConfirm = 'editConfirm',
  reactivated = 'reactivated',
  deleted = 'deleted',
  inserted = 'inserted'
}

export enum ACTION_BUTTON_TYPES {
  undo = 'undo',
  edit = 'edit',
  editConfirm = 'editConfirm',
  deactivate = 'deactivate',
  reactivate = 'reactivate',
  delete = 'delete',
  editUndo = 'editUndo',
  deactivateUndo = 'deactivateUndo',
  reactivateUndo = 'reactivateUndo',
  deleteUndo = 'deleteUndo',
  publish1 = 'publish1'
}

export const PAGE = {
  id: 'product-setup',
  name: 'Product Setup'
};

export const colWidth = 100;
export const HEADERS_ROWS_TAKEN = 1;
export const PADDING_BOTTOM_ROWS = 2;
export const SUBPRODUCTS_SEPARATOR = ',';

export interface IColumnDefinition {
  pos: number;
  key: string;
  displayName: string;
  colSize: number;
}
export const COLUMNS: IColumnDefinition[] = [
  { pos: 0, key: 'ProductName', displayName: 'Product Name', colSize: Math.round(colWidth * 1.3) },
  { pos: 1, key: 'Mnemonic', displayName: 'Mnemonic', colSize: Math.round(colWidth * 1.3) },
  { pos: 2, key: 'ProductNumber', displayName: 'Product Num', colSize: Math.round(colWidth * 1) },
  { pos: 3, key: 'ProductTypeText', displayName: 'Product Type', colSize: Math.round(colWidth) },
  { pos: 4, key: 'CounterpartyText', displayName: 'Counterparty Type', colSize: Math.round(colWidth) * 1.3 },
  { pos: 5, key: 'Description', displayName: 'Description', colSize: Math.round(colWidth * 2) },
  { pos: 6, key: 'ProductGroupName', displayName: 'GroupId', colSize: Math.round(colWidth * 1) },
  { pos: 7, key: 'DisplayOrder', displayName: 'Display Order', colSize: Math.round(colWidth * 1) },
  {
    pos: 8,
    key: 'SubProducts',
    displayName: 'ProductID List',
    // minimum colSize
    colSize: Math.round(colWidth * 3.25)
  },
  { pos: 9, key: '', displayName: 'Send To', colSize: Math.round(colWidth * 1) }
];

const actionButtonWidth = Math.round(colWidth * 0.4);
export const COLUMNS_ACTION_BUTTONS = [
  { pos: COLUMNS.length, key: 'ActionButton1', displayName: '', colSize: actionButtonWidth },
  { pos: COLUMNS.length + 1, key: 'ActionButton2', displayName: '', colSize: actionButtonWidth },
  { pos: COLUMNS.length + 2, key: 'ActionButton3', displayName: '', colSize: actionButtonWidth }
];

export const FILTER_OPTIONS = [
  {
    sheetName: 'Active Products',
    allowedRecordStatus: ProductStatus.Active
  },
  {
    sheetName: 'Inactive Products',
    allowedRecordStatus: ProductStatus.Inactive
  }
];

export const formIdKeys = {
  // ! They should be aligned with the API and DB values!
  delete: 'Delete Product',
  deactivate: 'Deactivate Product',
  reactivate: 'Reactivate Product',
  update: 'Update Product',
  create: 'New Product'
};

export const actionButtonsSettings = {
  iconBaseUrl: `/assets/img/action-button-icons/`,

  edit: { type: 1, iconName: 'edit', iconExt: 'png' },
  editConfirm: { type: 2, iconName: 'edit-confirm', iconExt: 'png' },
  deactivate: { type: 3, iconName: 'deactivate', iconExt: 'png' },
  reactivate: { type: 4, iconName: 'reactivate', iconExt: 'png' },
  delete: { type: 5, iconName: 'delete', iconExt: 'png' },
  editUndo: { type: 6, iconName: 'undo', iconExt: 'png' },
  deactivateUndo: { type: 7, iconName: 'undo', iconExt: 'png' },
  reactivateUndo: { type: 8, iconName: 'undo', iconExt: 'png' },
  deleteUndo: { type: 9, iconName: 'undo', iconExt: 'png' },
  publish1: { type: 10, iconName: 'publish1', iconExt: 'png' }
};

export const setActionButtonIcon = (cell: GC.Spread.Sheets.CellRange, iconImgUrl: string) => {
  cell.backgroundImage(iconImgUrl);
  cell.backgroundImageLayout(GC.Spread.Sheets.ImageLayout.stretch);
};

export const channelKeys = {
  saveSuccess: COMPONENT_TOAST_TYPES.saveSuccess,
  saveError: COMPONENT_TOAST_TYPES.saveError,
  loadError: COMPONENT_TOAST_TYPES.loadError,
  willCauseDataLoss: COMPONENT_TOAST_TYPES.willCauseDataLoss,
  actionDeactivate: COMPONENT_TOAST_TYPES.actionDeactivate,
  actionDeactivateUndo: COMPONENT_TOAST_TYPES.actionDeactivateUndo,
  saveClicked: 'save button', // save button clicked // ! dont modify
  discardClicked: 'discard button', // discard button clicked // ! dont modify
  publishedTab: COMPONENT_TOAST_TYPES.publishedTab,
  createTabError: COMPONENT_TOAST_TYPES.createTabError,
  // for event reactor channel
  validateSheetAndUpdateButtons: 'checkValidityAndUpdateButtons',
  deleteKeyPressed: 'deleteKeyPressed',
  loadData: 'loadData',
  undoRedoAction: 'undoRedoAction',
  showToast: 'showToast',
  valueChanged: 'valueChanged',
  checksOnModifications: 'checksOnModifications',
  // checkDataLoss: 'checkDataLoss',
  rowModified: 'rowModified',
  updateEditHistory: 'updateEditHistory',
  updateLocalStorage: 'updateLocalStorage',
  undo: 'undo',
  redo: 'redo',
  resizeOrRepaint: 'resizeOrRepaint',
  deleteRows: 'deleteRows',
  blankRowsCheck: 'blankRowsCheck',
  activeFilter: 'activeFilter',
  actionButtonClicked: 'actionButtonClicked'
};

export const workbookInit = args => {
  const spread: GC.Spread.Sheets.Workbook = args.spread;
  return spread;
};

export const isActiveTab = sheet => sheet.name() === FILTER_OPTIONS.find(opt => opt.allowedRecordStatus === ProductStatus.Active).sheetName;

export const clearSheet = (sheet: GC.Spread.Sheets.Worksheet) => {
  suspendDoAndResume(sheet, () => {
    // Clear Styles
    sheet.clear(
      HEADERS_ROWS_TAKEN,
      0,
      sheet.getRowCount() - HEADERS_ROWS_TAKEN,
      sheet.getColumnCount(), // Includes the buttons
      GC.Spread.Sheets.SheetArea.viewport,
      GC.Spread.Sheets.StorageType.style
    );

    // Clear Data
    sheet.clear(
      HEADERS_ROWS_TAKEN,
      0,
      sheet.getRowCount() - HEADERS_ROWS_TAKEN,
      sheet.getColumnCount(), // Includes the buttons
      GC.Spread.Sheets.SheetArea.viewport,
      GC.Spread.Sheets.StorageType.data
    );
  });
};

export const clearSheets = (spread: GC.Spread.Sheets.Workbook) => {
  const sheets = spread.sheets;
  sheets.forEach(clearSheet);
};

export const printSheetHeaders = (sheet: GC.Spread.Sheets.Worksheet, headers: { columns: string[]; rows: string[] }) => {
  COLUMNS.forEach((column, ind) => sheet.getCell(0, ind).text(column.displayName));

  if (headers.rows && headers.rows.length > 0) {
    headers.rows.forEach((headerName, row) => sheet.getCell(row, 0).text(headerName));
  }
};

export const setColumnWidths = (sheet: GC.Spread.Sheets.Worksheet, userRoleCanEdit: boolean) => {
  try {
    if (sheet && sheet.getColumnCount() >= COLUMNS.length) {
      const displayedColumns = [...COLUMNS];

      if (userRoleCanEdit) {
        displayedColumns.push(...COLUMNS_ACTION_BUTTONS);
      }

      // Content Columns (after action buttons set-up)
      const autoFitColumnKey = 'SubProducts';
      displayedColumns.forEach(column => {
        if (column.key !== autoFitColumnKey) {
          sheet.setColumnWidth(column.pos, column.colSize);
        } else {
          // ProductID List autocalculated width
          const pixelsAdjustment = 65;
          const takenWidth: number = +[...displayedColumns.filter(({ key }) => key !== autoFitColumnKey)].reduce(
            (acc, _column) => acc + sheet.getColumnWidth(_column.pos),
            pixelsAdjustment
          );
          const availableColWidth: number = Math.round(+getSpreadContainer().clientWidth - takenWidth);

          const subProductsColumn = COLUMNS.find(c => c.key === autoFitColumnKey);
          const fittedWidth = Math.max(availableColWidth, subProductsColumn.colSize);
          sheet.setColumnWidth(subProductsColumn.pos, fittedWidth);
        }
      });
      sheet.setColumnResizable(10, false, GC.Spread.Sheets.SheetArea.viewport);
      sheet.setColumnResizable(11, false, GC.Spread.Sheets.SheetArea.viewport);
      sheet.setColumnResizable(12, false, GC.Spread.Sheets.SheetArea.viewport);
      sheet.repaint();
    }
  } catch (error) {}
};

export const setSheetStyling = (sheet: GC.Spread.Sheets.Worksheet, products: ISetUpProduct[], userRoleCanEdit: boolean): void => {
  suspendDoAndResume(sheet, () => {
    const headerRowsRange = sheet.getRange(0, 0, HEADERS_ROWS_TAKEN, sheet.getColumnCount());
    const rowCountExistingRecords = products.filter(product => product.ProductId !== null && product.ProductId > -1).length;

    const actionButtonRange = sheet.getRange(
      HEADERS_ROWS_TAKEN,
      sheet.getColumnCount() - COLUMNS_ACTION_BUTTONS.length,
      sheet.getRowCount(),
      COLUMNS_ACTION_BUTTONS.length
    );

    const fullRange = sheet.getRange(0, 0, sheet.getRowCount(), sheet.getColumnCount());
    const editableRange = sheet.getRange(rowCountExistingRecords + 1, 0, sheet.getRowCount(), COLUMNS.length - 1);

    fullRange.cellPadding(`0 ${parseInt(FONT_SIZE, 10) / 2}`).vAlign(GC.Spread.Sheets.VerticalAlign.center);
    fullRange.locked(true);
    editableRange.locked(false);

    if (products && products.length > -1) {
      // Declaring borders
      sheet.defaults.colWidth = colWidth;

      // Products column width
      setColumnWidths(sheet, userRoleCanEdit);

      // Setting header styling
      headerRowsRange
        .font(`bold ${+FONT_SIZE + 1}pt ${FONT_NAME}`)
        .hAlign(GC.Spread.Sheets.HorizontalAlign.center)
        .backColor(colours.LIGHT_BLUE);

      // Action buttons
      actionButtonRange.hAlign(GC.Spread.Sheets.HorizontalAlign.center);
    }
  });
};

const allowExtendPasteRangeOnPasting = (spread: GC.Spread.Sheets.Workbook, sheet: GC.Spread.Sheets.Worksheet) => {
  sheet.options.isProtected = true;
  spread.options.allowExtendPasteRange = true;

  const style = sheet.getDefaultStyle();
  style.locked = false;
  sheet.setDefaultStyle(style);
};

export const setupLockOptions = (spread: GC.Spread.Sheets.Workbook, sheet: GC.Spread.Sheets.Worksheet) => {
  // Part of fix 3879 from Liquidity
  allowExtendPasteRangeOnPasting(spread, sheet); // sheet isProtected is included here

  // We need to sheet the options this way because those properties are
  // getters and setters. Otherwise we would overwrite them.
  sheet.options.protectionOptions.allowDeleteColumns = false;
  sheet.options.protectionOptions.allowDeleteRows = true;
  sheet.options.protectionOptions.allowDragInsertColumns = false;
  sheet.options.protectionOptions.allowDragInsertRows = false;
  sheet.options.protectionOptions.allowEditObjects = false;
  sheet.options.protectionOptions.allowFilter = false;
  sheet.options.protectionOptions.allowInsertColumns = false;
  sheet.options.protectionOptions.allowInsertRows = true;
  sheet.options.protectionOptions.allowResizeColumns = true;
  sheet.options.protectionOptions.allowResizeRows = false;
  sheet.options.protectionOptions.allowSelectLockedCells = true;
  sheet.options.protectionOptions.allowSelectUnlockedCells = true;
  sheet.options.protectionOptions.allowSort = false;
};

export const setUpWorkbook = (spread: GC.Spread.Sheets.Workbook, userRoleCanEdit: boolean) => {
  // Remove existing sheets
  for (let i = 0; i < spread.sheets.length; i++) {
    spread.removeSheet(i);
  }

  const wasSuspended = spread.isPaintSuspended();
  if (!wasSuspended) {
    spread.suspendPaint();
    spread.suspendEvent();
  }

  spread.options.allowContextMenu = true;
  spread.options.highlightInvalidData = true;
  spread.options.scrollbarMaxAlign = true;
  spread.options.scrollbarShowMax = true;
  spread.options.scrollIgnoreHidden = true;
  spread.options.showDragFillSmartTag = false;
  spread.options.showHorizontalScrollbar = true;
  spread.options.tabStripVisible = true;

  // Sheets are added and configured from product-setup-component @ PopulateData

  if (!wasSuspended) {
    spread.resumeEvent();
    spread.resumePaint();
  }
};

export const configureSheet = (spread: GC.Spread.Sheets.Workbook, sheet: GC.Spread.Sheets.Worksheet, userRoleCanEdit: boolean) => {
  if (sheet && spread) {
    const page = `${PAGE.id} ${sheet.name()}`;

    sheet.defaults.rowHeight = 20;
    sheet.defaults.colWidth = colWidth;
    sheet.options.allowCellOverflow = false;
    sheet.options.colHeaderVisible = true;

    if (userRoleCanEdit) {
      spread.options.allowCopyPasteExcelStyle = false; // true
      sheet.options.clipBoardOptions = GC.Spread.Sheets.ClipboardPasteOptions.values;
      spread.options.defaultDragFillType = GC.Spread.Sheets.Fill.AutoFillType.fillWithoutFormatting;
    }

    const columnCount = COLUMNS.length + (userRoleCanEdit ? COLUMNS_ACTION_BUTTONS.length : 0);
    sheet.setColumnCount(columnCount);

    setGreenArea({
      page,
      startRow: HEADERS_ROWS_TAKEN,
      startCol: 0,
      paddingRows: PADDING_BOTTOM_ROWS,
      paddingCols: COLUMNS_ACTION_BUTTONS.length
    });
    mapActionKeys(spread, sheet, page);

    // Set lock options for everything by default
    // Default options
    setupLockOptions(spread, sheet);
  } else {
    console.warn(`ERROR sheet or spread objects are invalid`);
  }
};

const populateColumn = (sheet: GC.Spread.Sheets.Worksheet, targetColumn: number, targetValues: any[]) => {
  sheet.setArray(HEADERS_ROWS_TAKEN, targetColumn, targetValues);
};

export const isBlankSetup = (setup: IProduct) => {
  const blankItem = ProductFactory.createProduct();
  // TODO: Fix using: !COLUMNS.keys.some(key => setup[key] !== blankItem[key]);
  const isBlank = !Object.keys(blankItem).some(key => setup[key] !== blankItem[key]);
  return isBlank;
};

export const filterSetupsBySheetName = (setups: IProduct[], sheetName: string) => {
  const allowedRS = FILTER_OPTIONS.find(opt => opt.sheetName === sheetName).allowedRecordStatus;
  return setups.filter(setup => setup.RecordStatus === allowedRS || isBlankSetup(setup));
};

export const populateProductSetups = (sheet: GC.Spread.Sheets.Worksheet, products: ISetUpProduct[], userRoleCanEdit: boolean) => {
  suspendDoAndResume(sheet, () => {
    // Clear previous data and styling
    clearSheet(sheet);
    // Headers
    printSheetHeaders(sheet, { columns: COLUMNS.map(c => c.displayName), rows: [] });
    // Once we have the list of product names we can trim the height of the sheet
    sheet.setRowCount(HEADERS_ROWS_TAKEN + products.length);

    // Populate each column
    let targetColumn = 0;
    if (sheet && products.length > -1) {
      populateColumn(
        sheet,
        targetColumn,
        products.map(setup => setup.ProductName)
      );
      targetColumn++;
      populateColumn(
        sheet,
        targetColumn,
        products.map(setup => setup.Mnemonic)
      );
      targetColumn++;
      populateColumn(
        sheet,
        targetColumn,
        products.map(setup => setup.ProductNumber)
      );
      targetColumn++;
      populateColumn(
        sheet,
        targetColumn,
        products.map(setup => setup.ProductTypeText)
      );
      targetColumn++;
      populateColumn(
        sheet,
        targetColumn,
        products.map(setup => setup.CounterpartyText)
      );
      targetColumn++;
      populateColumn(
        sheet,
        targetColumn,
        products.map(setup => setup.Description)
      );
      targetColumn++;
      populateColumn(
        sheet,
        targetColumn,
        products.map(setup => setup.ProductGroupName)
      );
      targetColumn++;
      populateColumn(
        sheet,
        targetColumn,
        products.map(setup => setup.DisplayOrder)
      );
      targetColumn++;
      populateColumn(
        sheet,
        targetColumn,
        products.map((setup: IProduct) => (!setup.SubProducts ? null : `${setup.SubProducts}`))
      );

      setSheetStyling(sheet, products, userRoleCanEdit);
    }
  });
};

export const validationMessages = (value: any) => [
  // ! Each element should be in the position of the proper column
  {
    // ProductName
    field: COLUMNS.find(c => c.key === 'ProductName').displayName,
    message: value
      ? `The ${COLUMNS.find(c => c.key === 'ProductName').displayName} should be text and cannot include wildcard characters as: ?, *, ~.`
      : `${COLUMNS.find(c => c.key === 'ProductName').displayName} cannot be empty.`
  },
  {
    // Mnemonic
    field: COLUMNS.find(c => c.key === 'Mnemonic').displayName,
    message: value
      ? // The Mnemonic should be unique, Alphanumeric and “_”
        `The ${COLUMNS.find(c => c.key === 'Mnemonic').displayName} should be Unique\nValid characters: a-z, A-Z, 0-9, _.`
      : `${COLUMNS.find(c => c.key === 'Mnemonic').displayName} cannot be empty.`
  },
  {
    // Product Number
    field: COLUMNS.find(c => c.key === 'ProductNumber').displayName,
    message:
      typeof value !== 'number' || (typeof value === 'number' && (value <= 0 || !Number.isInteger(value)))
        ? `The ${COLUMNS.find(c => c.key === 'ProductNumber').displayName} must be an integer greater than 0.`
        : typeof value === 'number' && value > MAX_PRODUCT_DIGITS * 10 - 1
        ? `The maximum digits for ${COLUMNS.find(c => c.key === 'ProductNumber').displayName} is ${MAX_PRODUCT_DIGITS}.`
        : !value
        ? `${COLUMNS.find(c => c.key === 'ProductNumber').displayName} cannot be empty.`
        : `Please introduce a valid ${COLUMNS.find(c => c.key === 'ProductNumber').displayName}.`
  },
  {
    // ProductTypeText
    field: COLUMNS.find(c => c.key === 'ProductTypeText').displayName,
    message: `The selected ${COLUMNS.find(c => c.key === 'ProductTypeText').displayName} is not available.`
  },
  {
    // CounterpartyText
    field: COLUMNS.find(c => c.key === 'CounterpartyText').displayName,
    message: `The selected ${COLUMNS.find(c => c.key === 'CounterpartyText').displayName} is not available.`
  },
  {
    // Description
    field: COLUMNS.find(c => c.key === 'Description').displayName,
    message: `${COLUMNS.find(c => c.key === 'Description').displayName} should contain a valid value.`
  },
  {
    // GroupId
    field: COLUMNS.find(c => c.key === 'ProductGroupName').displayName,
    message: value
      ? `${COLUMNS.find(c => c.key === 'ProductGroupName').displayName} should contain a valid value.`
      : `${COLUMNS.find(c => c.key === 'ProductGroupName').displayName} cannot be empty.`
  },
  {
    // Display Order
    field: COLUMNS.find(c => c.key === 'DisplayOrder').displayName,
    message: `${COLUMNS.find(c => c.key === 'DisplayOrder').displayName} should be numeric or blank.`
  },
  {
    // Subproducts
    field: COLUMNS.find(c => c.key === 'SubProducts').displayName,
    message: `${COLUMNS.find(c => c.key === 'SubProducts').displayName} should contain valid values.`
  }
];

// Row States and Action buttons
export const setIconToActionButtonCell = (type: ACTION_BUTTON_TYPES, buttonCell: GC.Spread.Sheets.CellRange, disabled = false) => {
  const { iconName, iconExt } = actionButtonsSettings[type];
  const finalIconUrl = `${actionButtonsSettings.iconBaseUrl}${iconName}${disabled ? `-disabled` : ''}.${iconExt}`;
  setActionButtonIcon(buttonCell, finalIconUrl);
};

export function removeItalicsToRange(sheet, range: GC.Spread.Sheets.CellRange) {
  let cell;
  for (let r = range.row; r < range.row + range.rowCount; r++) {
    for (let c = range.col; c < range.col + range.colCount; c++) {
      cell = sheet.getCell(r, c);
      const regular = cell.font().replace('italic ', '');
      cell.font(regular);
    }
  }
}

export function removeStrikeThroughFromRange(sheet, range) {
  range.textDecoration(GC.Spread.Sheets.TextDecorationType.none);
}

export function removeTextStyleFromRange(sheet, range: GC.Spread.Sheets.CellRange) {
  // Remove italics
  removeItalicsToRange(sheet, range);
  // Remove strikeThrough
  removeStrikeThroughFromRange(sheet, range);
  // Remove colours
  if (colourIndicatorsEnabled) {
    range.foreColor(null);
    range.backColor(null);
  }
}

export function setRowState(row: number, rowState: ROW_STATES, sheet, optionalParams = null, enableEdit: boolean, enableDelete: boolean) {
  const firstActionButtonColumn = COLUMNS.length;
  const actionButtonCell1 = sheet.getCell(row, firstActionButtonColumn);
  const actionButtonCell2 = sheet.getCell(row, firstActionButtonColumn + 1);
  const isActiveTabSelected = sheet.name() === FILTER_OPTIONS.find(opt => opt.allowedRecordStatus === ProductStatus.Active).sheetName;
  const editableRange = sheet.getRange(row, 0, 1, firstActionButtonColumn);

  suspendDoAndResume(sheet, () => {
    if (rowState === ROW_STATES.clean) {
      removeTextStyleFromRange(sheet, editableRange);
      if (isActiveTabSelected) {
        setIconToActionButtonCell(ACTION_BUTTON_TYPES.edit, actionButtonCell1, !enableEdit);
        setIconToActionButtonCell(ACTION_BUTTON_TYPES.deactivate, actionButtonCell2, !enableEdit);
      } /*if (!isActiveTabSelected)*/ else {
        setIconToActionButtonCell(ACTION_BUTTON_TYPES.reactivate, actionButtonCell1, !enableEdit);
        setIconToActionButtonCell(ACTION_BUTTON_TYPES.delete, actionButtonCell2, !enableDelete);
      }
      return;
    }
    if (rowState === ROW_STATES.editConfirm) {
      setRowState(row, ROW_STATES.clean, sheet, editableRange, enableEdit, enableDelete);
      addItalicsToRange(sheet, editableRange);
      if (colourIndicatorsEnabled) {
        editableRange.backColor(colours.LIGHT_BLUE);
        editableRange.foreColor(colours.DARK_BLUE);
      }
      return;
    }
    if (rowState === ROW_STATES.edit && optionalParams) {
      const { hasChanged, isValid, setup } = optionalParams;
      // If the row is blank, disable editConfirm button
      const rowIsBlank = !COLUMNS.some(column => setup[column.key] !== null);
      const editConfirmDisabled = rowIsBlank || !hasChanged || !isValid;

      const editUndoDisabled = false;
      // Disabled by default until some valueChange triggers
      setIconToActionButtonCell(ACTION_BUTTON_TYPES.editConfirm, actionButtonCell1, editConfirmDisabled);
      setIconToActionButtonCell(ACTION_BUTTON_TYPES.editUndo, actionButtonCell2, editUndoDisabled);
      return;
    }
    if (rowState === ROW_STATES.reactivated) {
      setIconToActionButtonCell(ACTION_BUTTON_TYPES.reactivateUndo, actionButtonCell1, false);
      setIconToActionButtonCell(ACTION_BUTTON_TYPES.delete, actionButtonCell2, true);
      if (colourIndicatorsEnabled) {
        editableRange.backColor(colours.LIGHT_GREEN);
      }
      return;
    }
    if (rowState === ROW_STATES.deleted) {
      setIconToActionButtonCell(ACTION_BUTTON_TYPES.deleteUndo, actionButtonCell1, false);
      setIconToActionButtonCell(ACTION_BUTTON_TYPES.delete, actionButtonCell2, true);
      addStrikeThroughToRange(sheet, editableRange);
      if (colourIndicatorsEnabled) {
        editableRange.backColor(colours.DARK_GREY);
      }
      return;
    }
    if (rowState === ROW_STATES.deactivated) {
      setIconToActionButtonCell(ACTION_BUTTON_TYPES.edit, actionButtonCell1, true); // It disables first button
      setIconToActionButtonCell(ACTION_BUTTON_TYPES.reactivate, actionButtonCell2, false);
      addStrikeThroughToRange(sheet, editableRange);
      if (colourIndicatorsEnabled) {
        editableRange.foreColor(colours.RED);
        editableRange.backColor(colours.LIGHT_RED);
      }
      return;
    }
    if (rowState === ROW_STATES.inserted) {
      // Add watermarks to the inserted row
      setRowWatermarks(row, sheet);
      return;
    }
  });
}

export function addStrikeThroughToRange(sheet, range: GC.Spread.Sheets.CellRange) {
  // Remove any style from the cell
  removeTextStyleFromRange(sheet, range);
  range.textDecoration(GC.Spread.Sheets.TextDecorationType.lineThrough);
}

export function addItalicsToRange(sheet, range: GC.Spread.Sheets.CellRange) {
  // Remove any style from the cell
  removeTextStyleFromRange(sheet, range);
  let cell;
  for (let r = range.row; r < range.row + range.rowCount; r++) {
    for (let c = range.col; c < range.col + range.colCount; c++) {
      cell = sheet.getCell(r, c);
      if (!cell.font().includes('italic')) {
        cell.font(`italic ${cell.font()}`);
      }
    }
  }
}

export function setRowWatermarks(row: number, sheet) {
  COLUMNS.forEach((column, col) => {
    const alignRight = false;
    const range = sheet.getRange(row, col, 1, 1);
    if (column.displayName) {
      setWatermark(range, column.displayName, alignRight);
    }
  });
}

/**
 * Populates icons to the spread sheet
 */
export const populateIcons = (sheet: GC.Spread.Sheets.Worksheet, tab: IProductTab, showActive: boolean) => {
  const wasSuspended = sheet.isPaintSuspended();
  if (!wasSuspended) {
    sheet.suspendPaint();
    sheet.suspendEvent();
  }

  const filteredProducts = tab.Products.filter(p => p && p.RecordStatus === (showActive ? ProductStatus.Active : ProductStatus.Inactive));

  filteredProducts.forEach((product, index) => {
    const row = index + 1;
    if (showActive) {
      const optionOneRange = sheet.getRange(row, 11);
      const optionTwoRange = sheet.getRange(row, 12);
      const optionThreeRange = sheet.getRange(row, 10);
      if (!product.ToBeDeactivated) {
        const optionOneIcon = ACTION_BUTTON_TYPES.edit;
        setIconToActionButtonCell(optionOneIcon, optionOneRange);

        const optionTwoIcon = ACTION_BUTTON_TYPES.deactivate;
        setIconToActionButtonCell(optionTwoIcon, optionTwoRange);

        const optionThreeIcon = ACTION_BUTTON_TYPES.publish1;
        setIconToActionButtonCell(optionThreeIcon, optionThreeRange);
      } else {
        const optionOneIcon = ACTION_BUTTON_TYPES.edit;
        setIconToActionButtonCell(optionOneIcon, optionOneRange);

        const optionTwoIcon = ACTION_BUTTON_TYPES.reactivate;
        setIconToActionButtonCell(optionTwoIcon, optionTwoRange);

        const optionThreeIcon = ACTION_BUTTON_TYPES.edit;
        setIconToActionButtonCell(optionThreeIcon, optionThreeRange);
      }
    } else {
      const optionOneRange = sheet.getRange(row, 11);
      const optionOneIcon = ACTION_BUTTON_TYPES.reactivate;
      setIconToActionButtonCell(optionOneIcon, optionOneRange);

      const optionTwoRange = sheet.getRange(row, 12);
      const optionTwoIcon = ACTION_BUTTON_TYPES.delete;
      setIconToActionButtonCell(optionTwoIcon, optionTwoRange);

      const optionThreeRange = sheet.getRange(row, 10);
      const optionThreeIcon = ACTION_BUTTON_TYPES.publish1;
      setIconToActionButtonCell(optionThreeIcon, optionThreeRange);

      sheet.setColumnVisible(9, false);
      sheet.setColumnVisible(10, false);
    }
  });

  if (!wasSuspended) {
    sheet.resumeEvent();
    sheet.resumePaint();
  }
};

export const setRowStates = (sheet: GC.Spread.Sheets.Worksheet, tab: IProductTab, showActiveProducts: boolean) => {
  const rowCount = sheet.getRowCount();

  const filteredProducts = tab.Products.filter(
    p => p && p.RecordStatus === (showActiveProducts ? ProductStatus.Active : ProductStatus.Inactive)
  );
  const dbFilteredProducts = tab.ProductsDB.filter(
    p => p && p.RecordStatus === (showActiveProducts ? ProductStatus.Active : ProductStatus.Inactive)
  );

  for (let index = 1; index < rowCount; index++) {
    const product = filteredProducts[index - 1];
    const productDb = dbFilteredProducts[index - 1];

    const rowRange = sheet.getRange(index, 0, 1, 9);

    if (product && product.ToBeDeactivated) {
      rowRange
        .foreColor(colours.RED)
        .backColor(colours.LIGHT_RED)
        .textDecoration(GC.Spread.Sheets.TextDecorationType.lineThrough);

      setIconToActionButtonCell(ACTION_BUTTON_TYPES.edit, sheet.getRange(index, 11, 1, 1));
      setIconToActionButtonCell(ACTION_BUTTON_TYPES.reactivate, sheet.getRange(index, 12, 1, 1));
    } else if (product && hasProductChanges(product, productDb)) {
      rowRange.foreColor(colours.DARK_BLUE).backColor(colours.BLUE);
    }
  }
};
